/**
 * PO Edit/Create Form
 *
 * Extracted from POManagementDialog. Contains all fields for editing or creating
 * a purchase order: PO number, description, cashflow fields, dates, and line items.
 */

import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Autocomplete,
  Box,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import EmailIcon from "@mui/icons-material/Email";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { POLineItem, WRO, WroAllocationsRequest, WroLineAllocations, WroLineAllocationsRequest, WroLineLink, WroLinkRequest, WroLinkSource, OrphanedAllocation, getPOLineItemsTotal } from "models/InventoryModel";
import { Supplier } from "models/CashflowModel";
import InventoryService, { CatalogSuggestions, NetSuiteDriftEntry } from "services/InventoryService";
import { shipbobWroUrl } from "shared/utils/shipbobLinks";
import { catalogPriceFor as catalogPriceForMethod, containerLabel } from "shared/utils/freightMethods";
import { fromPercent, linesWithoutTariff, suggestedTariffFor, toPercent, weightedTariffRate } from "shared/utils/tariffs";
import { missingPoFields } from "shared/utils/poCompleteness";
import ChargeSchedulePreview from "./ChargeSchedulePreview";
import CreateWroDialog from "./CreateWroDialog";
import UpdateWroDialog from "./UpdateWroDialog";
import EmailPoDialog from "./EmailPoDialog";
import PushToNetSuiteDialog from "./PushToNetSuiteDialog";
import ImportPoPdfDialog from "./ImportPoPdfDialog";
import WroAllocationsPanel, { PoOption } from "./WroAllocationsPanel";
import QuickAddSupplierDialog from "./QuickAddSupplierDialog";

export interface POEditFormProps {
  inventoryOptions: Array<{ id: string; name: string | null; skus: string[] }>;

  // Core PO fields
  editingPONumber: string;
  editingPODate: string;
  editingPODescription: string;
  editingPOLineItems: POLineItem[];

  // Cashflow fields
  suppliers: Supplier[];
  editingPOAmount?: number | null;
  editingPOSupplierId?: number | null;
  editingPOTariffRate?: number | null;
  editingPOStatus?: 'placed' | 'forecasted' | 'draft';
  editingPOBrand?: string | null;
  editingPOPlacementDate?: string | null;
  editingPOEtd?: string | null;

  // Callbacks
  onCancelEdit: () => void;
  onSetPONumber: (value: string) => void;
  onSetPODate: (value: string) => void;
  onSetPODescription: (value: string) => void;
  onAddLineItem: () => void;
  onUpdateLineItem: (index: number, updates: Partial<POLineItem>) => void;
  onRemoveLineItem: (index: number) => void;
  /** Swap the whole line list (PDF import, match-to-WRO) */
  onReplaceLineItems?: (items: POLineItem[]) => void;
  onSetPOAmount?: (value: number | null) => void;
  onSetPOSupplierId?: (value: number | null) => void;
  onSetPOTariffRate?: (value: number | null) => void;
  onSetPOStatus?: (value: 'placed' | 'forecasted' | 'draft') => void;
  onSetPOBrand?: (value: string | null) => void;
  onSetPOPlacementDate?: (value: string | null) => void;
  onSetPOEtd?: (value: string | null) => void;
  editingPOCountryOfDestination?: string | null;
  editingPOPaymentDateOverrides?: Record<string, string> | null;
  onSetPOCountryOfDestination?: (value: string | null) => void;
  onSetPOPaymentDateOverrides?: (value: Record<string, string> | null) => void;
  editingPOPaidTranches?: Record<string, boolean> | null;
  onTogglePaidTranche?: (trancheOrder: number, paid: boolean) => void;
  isUpdatingPaidStatus?: boolean;
  editingPOPriceType?: string | null;
  editingPOContainerType?: string | null;
  onSetPOPriceType?: (value: string | null) => void;
  onSetPOContainerType?: (value: string | null) => void;
  /** ShipBob receiving center the PO ships to (PDF ship-to, NetSuite address, WRO default) */
  editingPOShipToFcId?: number | null;
  onSetPOShipToFcId?: (value: number | null) => void;
  /** Current cost per inventory id — pre-fills a line's unit price on item select */
  /** SKU master prices + Danican product numbers — pre-fill and suggestions for PO lines */
  catalogSuggestions?: CatalogSuggestions | null;
  /** Fills the PO Number field with the server's suggested next number */
  onSuggestPONumber?: () => void;
  /** Downloads the PO document PDF (last saved version) for the PO being edited */
  onGeneratePdf?: () => void;
  /** Start a new PO as a copy of the one being edited (existing POs only) */
  onDuplicate?: () => void;
  /** Set while creating a PO that was started as a copy of another */
  duplicatedFromPoNumber?: string | null;
  isGeneratingPdf?: boolean;
  /** Set when the entered PO number is already used by a different PO */
  poNumberError?: string | null;

  // ShipBob WRO link — only offered while editing an existing PO
  wroOptions?: WRO[];
  /** WROs currently counting for the PO (matched by PO number, or the pinned one). */
  linkedWroIds?: string[];
  /** The WRO a person pinned, when wroLinkSource is 'manual' (null = pinned to none). */
  pinnedWroId?: string | null;
  wroLinkSource?: WroLinkSource;
  onLinkWRO?: (link: WroLinkRequest) => Promise<void>;
  /** Replace which WRO lines each PO line draws on (allocation editor); saves immediately */
  onSetWroAllocations?: (request: WroAllocationsRequest) => Promise<void>;
  /** Server-computed WRO links per line of the saved PO */
  wroLinksByInventoryId?: Record<string, WroLineLink[]>;
  /** Hand allocations per line that count nowhere any more (WRO cancelled or gone) */
  orphanedAllocationsByInventoryId?: Record<string, OrphanedAllocation[]>;
  /** Replace every allocation on one WRO, for every PO */
  onSetWroLineAllocations?: (wroId: string, lines: WroLineAllocationsRequest["lines"]) => Promise<void>;
  /** Other POs on the page (for handing part of a WRO line to one of them) */
  poOptions?: PoOption[];
  /** A supplier was added from the editor */
  onSuppliersChanged?: (suppliers: Supplier[]) => void;
  /** Refresh POs + WROs after a WRO was created in ShipBob; enables the Create WRO button */
  onWroCreated?: (wro: { wroId: string; fulfillmentCenterName: string | null }) => void;
  /** Only orders already linked by this tool support its update flow. */
  netsuiteCanUpdate?: boolean;
  /** Set once the PO exists in NetSuite (shows the link instead of the push button) */
  netsuiteLink?: { tranId: string; url: string } | null;
  /** Refresh POs after a push; enables the Push to NetSuite button */
  onNetSuitePushed?: (result: { netsuitePoId: string; tranId: string; url: string }) => void;
  /** Lines whose ShipBob item has look-alikes and is not yet confirmed */
  skuQuestionCount?: number;
  onConfirmSkus?: () => void;
  /** How this PO compares with its NetSuite record (null = not pushed / not checked) */
  netsuiteDrift?: NetSuiteDriftEntry | null;
  onRefreshNetSuiteDrift?: () => void;
  /** The PO's number as last saved (null while creating) — what the NetSuite name sync renames to */
  savedPONumber?: string | null;
  /** A save is in flight: leaving the editor is blocked until it finishes */
  isSaving?: boolean;
  /** The PO's saved amount when it was entered by hand (differs from its saved line total); else null */
  manualSavedAmount?: number | null;
}

const COUNTRY_OPTIONS = ['US', 'UK', 'CA', 'AU'] as const;
const PRICE_TYPE_OPTIONS = ['DDP', 'FOB'] as const;
const CONTAINER_OPTIONS = ['LCL', '20FT', '40FT', '40HC'] as const;

const formatUsd = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function POEditForm({
  inventoryOptions,
  editingPONumber,
  editingPODate,
  editingPODescription,
  editingPOLineItems,
  suppliers = [],
  editingPOAmount,
  editingPOSupplierId,
  editingPOTariffRate,
  editingPOStatus = 'placed',
  editingPOBrand,
  editingPOPlacementDate,
  editingPOEtd,
  onCancelEdit,
  onSetPONumber,
  onSetPODate,
  onSetPODescription,
  onAddLineItem,
  onUpdateLineItem,
  onRemoveLineItem,
  onReplaceLineItems,
  onSetPOAmount,
  onSetPOSupplierId,
  onSetPOTariffRate,
  onSetPOStatus,
  onSetPOBrand,
  onSetPOPlacementDate,
  onSetPOEtd,
  editingPOCountryOfDestination,
  editingPOPaymentDateOverrides,
  onSetPOCountryOfDestination,
  onSetPOPaymentDateOverrides,
  editingPOPaidTranches,
  onTogglePaidTranche,
  isUpdatingPaidStatus,
  editingPOPriceType,
  editingPOContainerType,
  onSetPOPriceType,
  onSetPOContainerType,
  editingPOShipToFcId = null,
  onSetPOShipToFcId,
  catalogSuggestions,
  onSuggestPONumber,
  onGeneratePdf,
  onDuplicate,
  duplicatedFromPoNumber = null,
  isGeneratingPdf,
  poNumberError,
  wroOptions = [],
  linkedWroIds = [],
  pinnedWroId = null,
  wroLinkSource = 'auto',
  onLinkWRO,
  onWroCreated,
  onSetWroAllocations,
  wroLinksByInventoryId = {},
  orphanedAllocationsByInventoryId = {},
  onSetWroLineAllocations,
  poOptions = [],
  onSuppliersChanged,
  netsuiteCanUpdate = true,
  netsuiteLink = null,
  netsuiteDrift = null,
  onRefreshNetSuiteDrift,
  savedPONumber = null,
  isSaving = false,
  manualSavedAmount = null,
  skuQuestionCount = 0,
  onConfirmSkus,
  onNetSuitePushed,
}: POEditFormProps): JSX.Element {
  const [isCreateWroOpen, setIsCreateWroOpen] = useState(false);
  const [isUpdateWroOpen, setIsUpdateWroOpen] = useState(false);
  const [isEmailPoOpen, setIsEmailPoOpen] = useState(false);
  const [isNetSuiteOpen, setIsNetSuiteOpen] = useState(false);
  const [isLinkWroOpen, setIsLinkWroOpen] = useState(false);
  // The WRO the action buttons (Edit in ShipBob, Update WRO…, Match) act on; picked in the panel
  const [selectedWroId, setSelectedWroId] = useState<string | null>(null);
  const [selectedWroOthers, setSelectedWroOthers] = useState<WroLineAllocations[]>([]);
  const [isAddSupplierOpen, setIsAddSupplierOpen] = useState(false);
  const [isImportPdfOpen, setIsImportPdfOpen] = useState(false);
  const [linkWroInput, setLinkWroInput] = useState("");
  const [isLinkingWro, setIsLinkingWro] = useState(false);
  const [linkWroError, setLinkWroError] = useState<string | null>(null);

  // ShipBob receiving centers for the Ship-to picker (fetched once)
  const inventoryService = useMemo(() => new InventoryService(), []);
  const [fulfillmentCenters, setFulfillmentCenters] = useState<Array<{ id: number; name: string; city: string | null; state: string | null; country: string | null; replacedById?: number | null }>>([]);
  useEffect(() => {
    if (!onSetPOShipToFcId) return;
    let isCancelled = false;
    inventoryService.getReceivingFulfillmentCenters().then(response => {
      if (!isCancelled && response) setFulfillmentCenters(response.fulfillmentCenters);
    });
    return () => { isCancelled = true; };
  }, [inventoryService, onSetPOShipToFcId]);
  const FC_COUNTRY_FOR_PO: Record<string, string> = { US: "US", UK: "GB", AU: "AU", CA: "CA" };
  const poFcCountry = FC_COUNTRY_FOR_PO[editingPOCountryOfDestination || "US"] || "US";
  const sortedFulfillmentCenters = [...fulfillmentCenters].sort((a, b) =>
    Number(b.country === poFcCountry) - Number(a.country === poFcCountry) || a.name.localeCompare(b.name));
  // Ship-to sanity checks. A center we no longer use (it stays selectable only on a PO already
  // saved with it), or one that differs from where this country's latest WRO actually went.
  const shipToFc = fulfillmentCenters.find(fc => fc.id === editingPOShipToFcId) ?? null;
  const shipToReplacement = shipToFc?.replacedById != null
    ? fulfillmentCenters.find(fc => fc.id === shipToFc.replacedById) ?? null : null;
  const inCountryFcNames = new Set(fulfillmentCenters.filter(fc => fc.country === poFcCountry && fc.replacedById == null).map(fc => fc.name));
  // WRO ids grow with creation, so the highest id is the latest WRO
  const latestInCountryWroFcName = [...wroOptions]
    .filter(w => w.status !== "Cancelled" && w.fulfillmentCenterName && inCountryFcNames.has(w.fulfillmentCenterName))
    .sort((a, b) => Number(b.wroId) - Number(a.wroId))[0]?.fulfillmentCenterName ?? null;
  const latestInCountryWroFc = fulfillmentCenters.find(fc => fc.name === latestInCountryWroFcName) ?? null;
  const shipToDiffersFromLatestWro = shipToFc != null && shipToFc.replacedById == null
    && shipToFc.country === poFcCountry && latestInCountryWroFc != null && latestInCountryWroFc.id !== shipToFc.id;

  // Link a WRO created by hand in ShipBob: import it into our mirror, pin the
  // PO to it, and refresh the page's WRO list.
  const handleLinkWroByNumber = async () => {
    const wroId = linkWroInput.trim();
    if (!/^\d+$/.test(wroId) || !onLinkWRO) return;
    setIsLinkingWro(true);
    setLinkWroError(null);
    try {
      const { wro } = await inventoryService.importWro(wroId);
      if (onSetWroAllocations) {
        // Allocate every PO line the WRO carries (whole lines), keeping other links as they are
        const existing: WroAllocationsRequest["allocations"] = [];
        for (const [inventoryId, links] of Object.entries(wroLinksByInventoryId)) {
          for (const link of links) if (link.linkKind === "allocation" && link.wroId !== wro.wroId) existing.push({ inventoryId, wroId: link.wroId, quantity: link.allocatedQuantity });
        }
        const added = editingPOLineItems
          .filter(li => li.inventoryId && wro.lines?.some((line: any) => String(line.inventoryId) === String(li.inventoryId)))
          .map(li => ({ inventoryId: String(li.inventoryId), wroId: wro.wroId, quantity: null }));
        await onSetWroAllocations({ autoMatch: wroLinkSource !== "manual", allocations: [...existing, ...added] });
        setSelectedWroId(wro.wroId);
      } else {
        await onLinkWRO({ source: "manual", wroId: wro.wroId });
      }
      onWroCreated?.({ wroId: wro.wroId, fulfillmentCenterName: wro.fulfillmentCenterName });
      setIsLinkWroOpen(false);
      setLinkWroInput("");
    } catch (err: any) {
      setLinkWroError(err.message);
    } finally {
      setIsLinkingWro(false);
    }
  };

  // Suggested price for a line: the SKU master's price for this PO's
  // container type (falling back through the other freight methods).
  const catalogPriceFor = (inventoryId?: string | null): number | undefined =>
    inventoryId ? catalogPriceForMethod(catalogSuggestions?.prices?.[inventoryId], editingPOContainerType) : undefined;
  const suggestedProductNumberFor = (inventoryId?: string | null): string | undefined =>
    inventoryId ? catalogSuggestions?.manufacturerSkus?.[inventoryId] : undefined;

  // Suggestions only apply to empty boxes — a typed value is never overwritten.
  const lineSuggestions = editingPOLineItems.map(li => ({
    price: li.unitPrice == null ? catalogPriceFor(li.inventoryId) : undefined,
    productNumber: !li.manufacturerProductNumber ? suggestedProductNumberFor(li.inventoryId) : undefined,
    tariff: li.tariffRate == null ? suggestedTariffFor(catalogSuggestions?.tariffs, li.inventoryId, editingPOSupplierId) : undefined,
  }));
  const pendingSuggestionCount = lineSuggestions.reduce(
    (count, sug) => count + (sug.price != null ? 1 : 0) + (sug.productNumber ? 1 : 0) + (sug.tariff ? 1 : 0), 0);
  const acceptAllSuggestions = () => {
    lineSuggestions.forEach((sug, index) => {
      if (sug.price == null && !sug.productNumber && !sug.tariff) return;
      onUpdateLineItem(index, {
        ...(sug.price != null && { unitPrice: sug.price }),
        ...(sug.productNumber && { manufacturerProductNumber: sug.productNumber }),
        ...(sug.tariff && { tariffRate: sug.tariff.rate }),
      });
    });
  };
  // The PO's tariff rate is derived from its lines (value-weighted), so the header
  // box only displays it; it is kept in the PO state for the charge-schedule preview.
  const weightedTariff = weightedTariffRate(editingPOLineItems);
  const weightedTariffPercent = toPercent(weightedTariff);
  useEffect(() => {
    if (editingPOTariffRate !== weightedTariffPercent) onSetPOTariffRate?.(weightedTariffPercent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightedTariffPercent]);
  const unratedLines = linesWithoutTariff(editingPOLineItems);
  // When every line carries a unit price the line items describe the PO, so the
  // amount comes from them. The one case that is NOT automatic is a PO that
  // already had a different amount entered by hand: that figure may cover
  // freight or duties the unit prices don't, so it is kept and both numbers are
  // shown until someone accepts the line total. "Already" means as last SAVED:
  // an amount that matched the lines then follows them when a quantity or price
  // changes here (it used to look hand-entered the moment a quantity changed, so
  // cashflow kept the old total). An amount typed into the field here counts as
  // entered by hand too — e.g. $12,000 with freight typed before the last unit
  // price, which would otherwise be replaced by the line total without a prompt.
  const computedAmount = getPOLineItemsTotal(editingPOLineItems);
  const sameMoney = (a: number, b: number) => Math.abs(a - b) <= 0.005;
  const [typedAmount, setTypedAmount] = useState<number | null>(null);
  useEffect(() => setTypedAmount(null), [savedPONumber]);
  const isHandEntered = (amount: number) => [manualSavedAmount, typedAmount].some(hand => hand != null && sameMoney(amount, hand));
  const storedAmountDiffers = editingPOAmount != null && isHandEntered(editingPOAmount)
    && computedAmount != null
    && !sameMoney(editingPOAmount, computedAmount);
  const amountIsFromLines = computedAmount != null && !storedAmountDiffers;
  const effectiveAmount = amountIsFromLines ? computedAmount : (editingPOAmount ?? null);

  // Keep the saved amount equal to what the locked field shows, so the value
  // sent on save is the computed one rather than whatever was there before.
  useEffect(() => {
    if (amountIsFromLines && editingPOAmount !== computedAmount) {
      onSetPOAmount?.(computedAmount);
    }
  }, [amountIsFromLines, computedAmount, editingPOAmount, onSetPOAmount]);

  // The WRO the buttons act on: the one selected in the allocations panel (it defaults to the
  // first linked WRO). Several WROs can feed a PO now, so there is no single "the" WRO.
  const pickerWro = wroOptions.find(w => w.wroId === selectedWroId) ?? null;
  // Items on the selected WRO that another PO takes — allocated by hand, or matched to it by
  // PO number (a consolidated container, or a WRO named for a different PO): this PO must not
  // pull them in or rewrite the WRO on its own.
  const selectedWroItemsTakenByOthers = new Set(
    selectedWroOthers
      .filter(line => line.allocations.some(a => a.poNumber !== editingPONumber)
        || line.autoMatchedPoNumbers.some(n => n !== editingPONumber))
      .map(line => line.inventoryId));
  const selectedWroSharedWithOthers = selectedWroItemsTakenByOthers.size > 0;

  // Danican often asks for quantity changes after the WRO exists. When the
  // linked WRO's expected quantities differ from the PO's current lines, offer
  // to sync it — only while ShipBob still allows cancelling (pre-arrival).
  const WRO_LANDED_STATUSES = new Set([
    "Arrived", "Partially Arrived", "Arrived At Hub", "Partially Arrived At Hub",
    "Processing At Hub", "Processing", "Completed", "Cancelled",
  ]);
  // Per-line differences between the PO and what its WROs will deliver to it: each line's
  // quantity against the sum of its shares across every linked WRO (a partial allocation
  // counts its share, not the whole WRO line). Landed WROs are settled and skipped. Items the
  // selected WRO carries that this PO lacks are listed too, unless another PO has taken them.
  const wroLineDiffs = (() => {
    const diffs: Array<{ index: number; name: string; poQty: number; wroQty: number | null }> = [];
    const anyLinks = Object.values(wroLinksByInventoryId).some(links => links.length > 0);
    if (!anyLinks && !pickerWro) return diffs;
    editingPOLineItems.forEach((li, index) => {
      if (li.quantity < 1 || !li.inventoryId) return;
      const links = (wroLinksByInventoryId[String(li.inventoryId)] ?? []).filter(link => !WRO_LANDED_STATUSES.has(link.status ?? ""));
      if (links.length === 0) {
        // No open WRO carries this line — only worth flagging when the selected WRO is open
        if (pickerWro && !WRO_LANDED_STATUSES.has(pickerWro.status)) diffs.push({ index, name: li.inventoryName || li.inventoryId, poQty: li.quantity, wroQty: null });
        return;
      }
      const share = links.reduce((sum, link) => sum + link.expectedQuantity, 0);
      if (share !== li.quantity) diffs.push({ index, name: li.inventoryName || li.inventoryId, poQty: li.quantity, wroQty: share });
    });
    if (pickerWro && !WRO_LANDED_STATUSES.has(pickerWro.status)) {
      for (const line of pickerWro.lines) {
        const id = String(line.inventoryId);
        if (selectedWroItemsTakenByOthers.has(id)) continue;
        if (!editingPOLineItems.some(li => String(li.inventoryId) === id && li.quantity >= 1)) {
          diffs.push({ index: -1, name: line.sku || id, poQty: 0, wroQty: line.expectedQuantity });
        }
      }
    }
    return diffs;
  })();
  const wroQuantityMismatch = wroLineDiffs.length > 0;
  // Set the PO's lines to what the WRO says (for WROs edited in ShipBob):
  // quantities follow the WRO, lines only on the WRO are added, lines the WRO
  // dropped are removed. The WRO carries no prices, so a new or unpriced line
  // takes the SKU master's price and Danican product number; typed prices stay.
  const matchPoToWro = () => {
    if (!pickerWro) return;
    if (!onReplaceLineItems) {
      for (const diff of wroLineDiffs) {
        if (diff.index >= 0 && diff.wroQty != null) onUpdateLineItem(diff.index, { quantity: diff.wroQty });
      }
      return;
    }
    const wroLineById = new Map(pickerWro.lines.map(line => [String(line.inventoryId), line]));
    // A line's quantity becomes its share across every linked WRO (all of the WRO line unless
    // a person allocated part of it); lines on no WRO at all are dropped.
    const shareOf = (inventoryId: string): number | null => {
      const links = wroLinksByInventoryId[inventoryId] ?? [];
      return links.length > 0 ? links.reduce((sum, link) => sum + link.expectedQuantity, 0) : null;
    };
    const kept: POLineItem[] = editingPOLineItems
      .filter(li => !li.inventoryId || shareOf(String(li.inventoryId)) != null || wroLineById.has(String(li.inventoryId)))
      .map(li => {
        const id = String(li.inventoryId);
        const share = li.inventoryId ? shareOf(id) : null;
        const wroLine = li.inventoryId && share == null && !selectedWroItemsTakenByOthers.has(id) ? wroLineById.get(id) : undefined;
        const quantity = share ?? wroLine?.expectedQuantity;
        return {
          ...li,
          ...(quantity != null && { quantity, skuConfirmed: true }),
          unitPrice: li.unitPrice ?? catalogPriceFor(li.inventoryId) ?? null,
          manufacturerProductNumber: li.manufacturerProductNumber || suggestedProductNumberFor(li.inventoryId) || null,
        };
      });
    const present = new Set(kept.map(li => String(li.inventoryId)));
    const added: POLineItem[] = pickerWro.lines
      .filter(line => !present.has(String(line.inventoryId)) && !selectedWroItemsTakenByOthers.has(String(line.inventoryId)))
      .map(line => {
        const id = String(line.inventoryId);
        return {
          inventoryId: id,
          inventoryName: inventoryOptions.find(opt => opt.id === id)?.name ?? line.sku ?? undefined,
          quantity: line.expectedQuantity,
          unitPrice: catalogPriceFor(id) ?? null,
          manufacturerProductNumber: suggestedProductNumberFor(id) ?? null,
          // ShipBob's WRO is the source of truth for which item is arriving
          skuConfirmed: true,
        };
      });
    onReplaceLineItems([...kept, ...added]);
  };
  // ShipBob lets a WRO be edited in its dashboard only while Awaiting Arrival
  // (and on prepaid labels — all our freight); the arrival date locks 5 days out.
  const wroEditableInShipBob = pickerWro?.status === "Awaiting";
  const wroDaysToEta = pickerWro?.expectedArrivalDate && !pickerWro.expectedArrivalDate.startsWith("9999")
    ? moment(pickerWro.expectedArrivalDate).diff(moment().startOf("day"), "days")
    : null;
  const wroDateLocked = wroDaysToEta != null && wroDaysToEta < 5;
  // Printing, emailing, NetSuite and the WRO all restock whatever item the line
  // names, so none of them run while a line's item is unconfirmed among look-alikes.
  const skuGateReason = skuQuestionCount > 0
    ? `${skuQuestionCount} line${skuQuestionCount === 1 ? "" : "s"} point at an item with look-alikes — confirm the SKUs first (Save asks you)`
    : null;
  // Prices, container and ship-to are required before a PO is saved as placed or
  // pushed to NetSuite (the server refuses too). Drafts are exempt from saving, not from pushing.
  const missingFields = missingPoFields({
    lineItems: editingPOLineItems,
    containerType: editingPOContainerType,
    shipToFcId: editingPOShipToFcId,
  });
  const netsuiteGateReason = skuGateReason
    ?? (editingPOStatus === 'draft' ? "This PO is a draft — save it as placed first" : null)
    ?? (missingFields.length > 0 ? `Fill in ${missingFields.join(', ')} first — NetSuite needs them` : null);
  const selectedSupplier = suppliers.find(s => s.id === editingPOSupplierId);
  // NetSuite name sync after a rename here: one confirmed call, then re-read the NetSuite status
  const [isRenamingInNetSuite, setIsRenamingInNetSuite] = useState(false);
  const [netsuiteRenameError, setNetsuiteRenameError] = useState<string | null>(null);
  const handleRenameInNetSuite = async () => {
    if (!savedPONumber || !netsuiteDrift) return;
    if (!window.confirm(`Rename ${netsuiteDrift.tranId} to ${netsuiteDrift.expectedTranId} in NetSuite? Only the PO number changes.`)) return;
    setIsRenamingInNetSuite(true);
    setNetsuiteRenameError(null);
    try {
      await inventoryService.renameNetSuitePo(savedPONumber);
      onRefreshNetSuiteDrift?.();
    } catch (err: any) {
      setNetsuiteRenameError(err?.message || String(err));
    } finally {
      setIsRenamingInNetSuite(false);
    }
  };

  const exceedsMaxOrder = selectedSupplier?.maxOrderAmount != null
    && effectiveAmount != null
    && effectiveAmount > selectedSupplier.maxOrderAmount;

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={onCancelEdit}
          disabled={isSaving}
        >
          Back to Purchase Orders
        </Button>
        {(onGeneratePdf || onDuplicate) && (
          <Box sx={{ display: "flex", gap: 1 }}>
            {onDuplicate && (
              <Tooltip title="Start a new PO with these lines, supplier and shipping details. The number, dates and WRO/NetSuite links are not copied.">
                <Button size="small" variant="outlined" startIcon={<ContentCopyIcon />} onClick={onDuplicate}>
                  Duplicate
                </Button>
              </Tooltip>
            )}
            {onGeneratePdf && <Tooltip title={skuGateReason ?? "Downloads the last saved version — save first to include current edits"}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  disabled={isGeneratingPdf || skuGateReason != null}
                  onClick={onGeneratePdf}
                >
                  {isGeneratingPdf ? "Generating…" : "Download PDF"}
                </Button>
              </span>
            </Tooltip>}
            {onGeneratePdf && <Tooltip title={skuGateReason ?? "Emails the last saved version's PDF to the supplier — you review the draft first"}>
              <span>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<EmailIcon />}
                  disabled={skuGateReason != null}
                  onClick={() => setIsEmailPoOpen(true)}
                >
                  Email PO
                </Button>
              </span>
            </Tooltip>}
            {onNetSuitePushed && (
              netsuiteLink ? (
                <>
                  <Tooltip title={`Open ${netsuiteLink.tranId} in NetSuite`}>
                    <Button
                      size="small"
                      variant="outlined"
                      color="success"
                      startIcon={<OpenInNewIcon />}
                      href={netsuiteLink.url}
                      target="_blank"
                      rel="noopener"
                    >
                      {netsuiteLink.tranId}
                    </Button>
                  </Tooltip>
                  <Tooltip title={!netsuiteCanUpdate ? "This PO was created outside this tool. Open it in NetSuite to make changes." : netsuiteGateReason ?? "Re-send this PO's lines, dates, memo and ship-to to NetSuite — save first, you review before it sends"}>
                    <span>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        disabled={!netsuiteCanUpdate || netsuiteGateReason != null}
                        onClick={() => setIsNetSuiteOpen(true)}
                      >
                        Update in NetSuite
                      </Button>
                    </span>
                  </Tooltip>
                </>
              ) : (
                <Tooltip title={netsuiteGateReason ?? "Creates this PO in NetSuite — you review the lines first"}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={netsuiteGateReason != null}
                      onClick={() => setIsNetSuiteOpen(true)}
                    >
                      Push to NetSuite
                    </Button>
                  </span>
                </Tooltip>
              )
            )}
          </Box>
        )}
      </Box>

      {duplicatedFromPoNumber && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Started as a copy of <strong>{duplicatedFromPoNumber}</strong>: its lines, prices, supplier, container and ship-to are filled in.
          Give it a new number and dates; the source's WRO, NetSuite and receipt links are not carried over. Nothing is saved until you save.
        </Alert>
      )}

      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
        <TextField
          size="small"
          label="PO Number"
          value={editingPONumber}
          onChange={(e) => onSetPONumber(e.target.value)}
          required
          error={Boolean(poNumberError)}
          helperText={poNumberError || undefined}
          sx={{ width: 200 }}
        />
        {onSuggestPONumber && (
          <Button size="small" variant="outlined" onClick={onSuggestPONumber}>
            Suggest #
          </Button>
        )}
        <TextField
          size="small"
          label="Description"
          value={editingPODescription}
          onChange={(e) => onSetPODescription(e.target.value)}
          sx={{ width: 300, flexGrow: 1 }}
        />
      </Box>

      {/* Cashflow Fields */}
      <Box sx={{ mb: 3 }}>
        {storedAmountDiffers && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button
                size="small"
                color="inherit"
                onClick={() => onSetPOAmount?.(computedAmount)}
              >
                Use {formatUsd(computedAmount as number)}
              </Button>
            }
          >
            The line items add up to <strong>{formatUsd(computedAmount as number)}</strong>, but
            this PO's amount is <strong>{formatUsd(editingPOAmount as number)}</strong>. The
            entered amount is being kept — it may cover freight or duties the unit prices don't.
            Cashflow uses whichever amount is saved here.
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap", alignItems: "center" }}>
          <TextField
            size="small"
            label={amountIsFromLines ? "Amount (from line items)" : "Amount"}
            type="number"
            value={effectiveAmount ?? ""}
            onChange={(e) => {
              const val = e.target.value === "" ? null : parseFloat(e.target.value);
              setTypedAmount(val);
              onSetPOAmount?.(val);
            }}
            disabled={amountIsFromLines}
            InputProps={{
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
            }}
            sx={{ width: 190 }}
          />
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Pricing</InputLabel>
            <Select
              value={editingPOPriceType ?? ""}
              label="Pricing"
              onChange={(e) => onSetPOPriceType?.(e.target.value || null)}
            >
              <MenuItem value="">None</MenuItem>
              {PRICE_TYPE_OPTIONS.map(pt => (
                <MenuItem key={pt} value={pt}>{pt}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Container</InputLabel>
            <Select
              value={editingPOContainerType ?? ""}
              label="Container"
              onChange={(e) => onSetPOContainerType?.(e.target.value || null)}
            >
              <MenuItem value="">None</MenuItem>
              {CONTAINER_OPTIONS.map(ct => (
                <MenuItem key={ct} value={ct}>{containerLabel(ct)}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {onSetPOShipToFcId && (
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel>Ship to (ShipBob center)</InputLabel>
              <Select
                value={editingPOShipToFcId ?? ""}
                label="Ship to (ShipBob center)"
                onChange={(e) => onSetPOShipToFcId(e.target.value === "" ? null : Number(e.target.value))}
              >
                <MenuItem value="">Default for {editingPOCountryOfDestination || "US"}</MenuItem>
                {sortedFulfillmentCenters.filter(fc => fc.replacedById == null || fc.id === editingPOShipToFcId).map(fc => (
                  <MenuItem key={fc.id} value={fc.id}>
                    {fc.name}{fc.city ? ` — ${fc.city}${fc.state ? `, ${fc.state}` : ""}` : ""}{fc.country !== poFcCountry ? ` (${fc.country})` : ""}{fc.replacedById != null ? " — no longer used" : ""}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {onSetPOShipToFcId && shipToFc && shipToFc.replacedById != null && (
            <Alert
              severity="error"
              sx={{ width: "100%" }}
              action={shipToReplacement && (
                <Button color="inherit" size="small" onClick={() => onSetPOShipToFcId(shipToReplacement.id)}>
                  Switch to {shipToReplacement.name}
                </Button>
              )}
            >
              <strong>{shipToFc.name} is no longer used.</strong> ShipBob rejects WROs sent there, and it prints on the PO
              document and goes to NetSuite as the delivery address.
              {shipToReplacement ? ` Use ${shipToReplacement.name} instead.` : ""}
            </Alert>
          )}
          {onSetPOShipToFcId && shipToDiffersFromLatestWro && shipToFc && latestInCountryWroFc && (
            <Alert
              severity="warning"
              sx={{ width: "100%" }}
              action={
                <Button color="inherit" size="small" onClick={() => onSetPOShipToFcId(latestInCountryWroFc.id)}>
                  Switch to {latestInCountryWroFc.name}
                </Button>
              }
            >
              This PO ships to <strong>{shipToFc.name}</strong>, but the latest {editingPOCountryOfDestination || "US"} WRO went
              to <strong>{latestInCountryWroFc.name}</strong>. Check the ship-to is still right.
            </Alert>
          )}
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Supplier</InputLabel>
            <Select
              value={editingPOSupplierId ?? ""}
              label="Supplier"
              onChange={(e) => {
                if (e.target.value === "__add__") return;
                const val = e.target.value === "" ? null : Number(e.target.value);
                onSetPOSupplierId?.(val);
              }}
            >
              <MenuItem value="">None</MenuItem>
              {suppliers.map(s => (
                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
              ))}
              {onSuppliersChanged && (
                <MenuItem value="__add__" onClick={() => setIsAddSupplierOpen(true)} sx={{ fontStyle: "italic" }}>
                  + Add supplier…
                </MenuItem>
              )}
            </Select>
          </FormControl>
          {onSuppliersChanged && (
            <QuickAddSupplierDialog
              open={isAddSupplierOpen}
              onClose={() => setIsAddSupplierOpen(false)}
              existing={suppliers}
              onCreated={(list, created) => { onSuppliersChanged(list); onSetPOSupplierId?.(created.id); }}
            />
          )}
          <Tooltip title={weightedTariffPercent != null
            ? "Weighted average of the line tariff rates (by line value). Set each line's rate in the table below."
            : "Set tariff rates on the lines below; the PO's rate is their weighted average."}>
            <TextField
              size="small"
              label="Tariff Rate"
              value={weightedTariffPercent ?? ""}
              placeholder="from lines"
              InputProps={{
                readOnly: true,
                endAdornment: <InputAdornment position="end">%</InputAdornment>,
              }}
              sx={{ width: 120, "& .MuiInputBase-input": { color: "text.secondary" } }}
            />
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={editingPOBrand ?? ""}
              label="Brand"
              onChange={(e) => onSetPOBrand?.(e.target.value || null)}
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Dosaze">Dosaze</MenuItem>
              <MenuItem value="Noonlash">Noonlash</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Destination</InputLabel>
            <Select
              value={editingPOCountryOfDestination ?? ""}
              label="Destination"
              onChange={(e) => onSetPOCountryOfDestination?.(e.target.value || null)}
            >
              <MenuItem value="">None</MenuItem>
              {COUNTRY_OPTIONS.map(c => (
                <MenuItem key={c} value={c}>{c}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={editingPOStatus}
            onChange={(_, val) => { if (val) onSetPOStatus?.(val); }}
          >
            <ToggleButton value="placed" sx={{ textTransform: 'none' }}>Placed</ToggleButton>
            <ToggleButton value="forecasted" sx={{ textTransform: 'none' }}>Forecasted</ToggleButton>
            <Tooltip title="A draft is kept but counts nowhere — not in the forecast, cashflow, or NetSuite — until it is saved as placed">
              <ToggleButton value="draft" sx={{ textTransform: 'none' }}>Draft</ToggleButton>
            </Tooltip>
          </ToggleButtonGroup>
        </Box>
        {exceedsMaxOrder && selectedSupplier && (
          <Alert severity="error" sx={{ mb: 2, backgroundColor: '#FEE2E2', color: '#991B1B' }}>
            Amount exceeds {selectedSupplier.name}'s max order of ${selectedSupplier.maxOrderAmount!.toLocaleString()} per PO
          </Alert>
        )}
        <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap" }}>
          <DatePicker
            label="Arrive By Date"
            value={moment(editingPODate)}
            onChange={(newValue: any) => {
              if (newValue && newValue.isValid()) {
                onSetPODate(newValue.format("YYYY-MM-DD"));
              }
            }}
            slotProps={{
              textField: {
                size: "small",
                required: true,
                sx: { width: 180 },
                helperText: "Setting a future date overrides the forecast's own estimate",
              },
            }}
          />
          <DatePicker
            label="Placement Date"
            value={editingPOPlacementDate ? moment(editingPOPlacementDate) : null}
            onChange={(newValue: any) => {
              onSetPOPlacementDate?.(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null);
            }}
            slotProps={{
              textField: { size: "small", sx: { width: 180 } },
            }}
          />
          <DatePicker
            label="ETD"
            value={editingPOEtd ? moment(editingPOEtd) : null}
            onChange={(newValue: any) => {
              onSetPOEtd?.(newValue && newValue.isValid() ? newValue.format("YYYY-MM-DD") : null);
            }}
            slotProps={{
              textField: { size: "small", sx: { width: 180 } },
            }}
          />
          {onLinkWRO && (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, flexWrap: "wrap" }}>
              {onWroCreated && pickerWro && (
                <Tooltip title={wroEditableInShipBob
                  ? `Awaiting Arrival — products and quantities can be edited in ShipBob${wroDateLocked ? "; the arrival date is locked (within 5 days of ETA)" : wroDaysToEta != null ? `; the arrival date can still be changed for ${wroDaysToEta - 5} more day${wroDaysToEta - 5 === 1 ? "" : "s"}` : ""}`
                  : `ShipBob only allows edits while a WRO is Awaiting Arrival — this one is ${pickerWro.status}`}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                      disabled={!wroEditableInShipBob}
                      startIcon={<OpenInNewIcon />}
                      href={shipbobWroUrl(pickerWro.wroId)}
                      target="_blank"
                      rel="noopener"
                    >
                      Edit in ShipBob
                    </Button>
                  </span>
                </Tooltip>
              )}
              {onWroCreated && pickerWro && (
                <Tooltip title={`Edit WRO ${pickerWro.wroId} in ShipBob from here — quantities, arrival date, its PO #, or the container number once you have it. Same WRO number, same box labels.`}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                      onClick={() => setIsUpdateWroOpen(true)}
                    >
                      Update WRO…
                    </Button>
                  </span>
                </Tooltip>
              )}
              {onWroCreated && (
                <Tooltip title={skuGateReason ?? (pickerWro
                  ? `Creates a SECOND receiving order in ShipBob alongside WRO ${pickerWro.wroId} — for a PO split across shipments`
                  : "Creates a receiving order in ShipBob for this PO")}>
                  <span>
                    <Button
                      size="small"
                      variant={pickerWro ? "text" : "outlined"}
                      sx={{ mt: 0.5, whiteSpace: "nowrap" }}
                      disabled={skuGateReason != null}
                      onClick={() => setIsCreateWroOpen(true)}
                    >
                      {pickerWro ? "Create new WRO…" : "Create WRO…"}
                    </Button>
                  </span>
                </Tooltip>
              )}
              {onWroCreated && (
                <Tooltip title="Link a WRO that was created in the ShipBob dashboard — pulls it in right away instead of waiting for the hourly sync">
                  <Button size="small" sx={{ mt: 0.5, whiteSpace: "nowrap" }} onClick={() => { setIsLinkWroOpen(true); setLinkWroError(null); }}>
                    Link by WRO #…
                  </Button>
                </Tooltip>
              )}
            </Box>
          )}
        </Box>
        <Box>
          {onLinkWRO && (
            <WroAllocationsPanel
              poNumber={editingPONumber}
              lineItems={editingPOLineItems}
              wroLinksByInventoryId={wroLinksByInventoryId}
              orphanedAllocationsByInventoryId={orphanedAllocationsByInventoryId}
              wroOptions={wroOptions}
              autoMatch={wroLinkSource !== 'manual'}
              selectedWroId={selectedWroId}
              onSelectWro={setSelectedWroId}
              poOptions={poOptions}
              onSaveAutoMatch={onSetWroAllocations ?? (async (request) => {
                // Older pages without the allocation endpoint: only the auto-match switch is supported
                await onLinkWRO(request.autoMatch ? { source: 'auto' } : { source: 'manual', wroId: null });
              })}
              onSaveWroLines={onSetWroLineAllocations ?? (async () => { throw new Error("Allocations can't be edited from this page"); })}
              onWroAllocationsLoaded={(wroId, lines) => { if (wroId === selectedWroId) setSelectedWroOthers(lines); }}
            />
          )}
          {onWroCreated && wroQuantityMismatch && pickerWro && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                  {(onReplaceLineItems || wroLineDiffs.some(d => d.index >= 0 && d.wroQty != null)) && (
                    <Tooltip title="Set this PO's lines to the WRO's: quantities follow the WRO, missing lines are added with the SKU master's price and product #, dropped lines are removed. Then save, and update NetSuite if it's there.">
                      <Button color="inherit" size="small" onClick={matchPoToWro}>Match PO to WRO</Button>
                    </Tooltip>
                  )}
                  {onReplaceLineItems && (
                    <Tooltip title="Read the PO PDF we sent the manufacturer and match this PO to it instead">
                      <Button color="inherit" size="small" onClick={() => setIsImportPdfOpen(true)}>Match to PO PDF…</Button>
                    </Tooltip>
                  )}
                  {!selectedWroSharedWithOthers && (
                    <Button color="inherit" size="small" onClick={() => setIsUpdateWroOpen(true)}>Update WRO…</Button>
                  )}
                </Box>
              }
            >
              <strong>The linked WRO quantities differ from this PO</strong> — a WRO may have been edited in ShipBob, or this PO takes only part of a shared container.
              <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                {wroLineDiffs.map(d => (
                  <li key={`${d.index}-${d.name}`}>{d.name}: PO {d.poQty} · WRO {d.wroQty ?? "not on WRO"}</li>
                ))}
              </Box>
            </Alert>
          )}
          {unratedLines > 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              {unratedLines === editingPOLineItems.filter(li => li.inventoryId).length
                ? "No tariff rate on the lines — cashflow counts this PO as 0% duty until they're filled in."
                : `No tariff rate on ${unratedLines} line${unratedLines === 1 ? "" : "s"} — the PO's rate is the average of the rated lines, so cashflow applies that to the whole PO until they're filled in.`}
              {" "}Saving and NetSuite are not blocked.
            </Alert>
          )}
          {missingFields.length > 0 && (
            <Alert severity={editingPOStatus === 'draft' ? "info" : "warning"} sx={{ mb: 2 }}>
              {editingPOStatus === 'draft' ? (
                <><strong>Draft.</strong> Still missing: {missingFields.join(", ")}. Fill them in and "Save as placed" for it to count in the forecast, cashflow, and NetSuite.</>
              ) : (
                <><strong>Fill in {missingFields.join(", ")} before saving</strong> — or "Save as draft" to keep what you have without it counting anywhere yet.</>
              )}
            </Alert>
          )}
          {skuQuestionCount > 0 && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={onConfirmSkus && <Button color="inherit" size="small" onClick={onConfirmSkus}>Confirm SKUs…</Button>}
            >
              <strong>{skuQuestionCount} line{skuQuestionCount === 1 ? "" : "s"} point at an item that has look-alikes</strong> — an older listing,
              the same product from another factory, or another item sharing its Danican #. Confirm which item this order restocks
              before saving, printing, emailing, or pushing it.
            </Alert>
          )}
          {savedPONumber && netsuiteDrift?.nameDiffers && !netsuiteDrift.missing && (
            <Alert
              severity="warning"
              sx={{ mb: 2 }}
              action={
                <Tooltip title={editingPONumber.trim() !== savedPONumber
                  ? "Save the PO first — NetSuite gets the saved number"
                  : `Changes only the PO number in NetSuite, from ${netsuiteDrift.tranId} to ${netsuiteDrift.expectedTranId}. Lines, prices and dates are not touched.`}>
                  <span>
                    <Button
                      color="inherit"
                      size="small"
                      disabled={isRenamingInNetSuite || editingPONumber.trim() !== savedPONumber}
                      onClick={handleRenameInNetSuite}
                    >
                      {isRenamingInNetSuite ? "Renaming…" : "Rename in NetSuite"}
                    </Button>
                  </span>
                </Tooltip>
              }
            >
              <strong>NetSuite still calls this PO {netsuiteDrift.tranId}</strong> — it was renamed here, so the
              NetSuite number should now be {netsuiteDrift.expectedTranId}. The link is kept either way.
              {netsuiteRenameError && <Box sx={{ mt: 0.5 }}>Rename failed: {netsuiteRenameError}</Box>}
            </Alert>
          )}
          {netsuiteLink && netsuiteDrift?.differs && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              action={
                !netsuiteDrift.missing && (
                  <Tooltip title="Re-send this PO's saved lines to NetSuite — save first if you changed anything here">
                    <Button color="inherit" size="small" onClick={() => setIsNetSuiteOpen(true)}>Update in NetSuite</Button>
                  </Tooltip>
                )
              }
            >
              {netsuiteDrift.missing ? (
                <><strong>NetSuite no longer has {netsuiteDrift.tranId}</strong> — the linked record could not be found; it may have been deleted there.</>
              ) : (
                <>
                  <strong>{netsuiteDrift.tranId} in NetSuite differs from this PO</strong>
                  {netsuiteDrift.lastModified ? ` (NetSuite last changed ${netsuiteDrift.lastModified})` : ""} — compared against the last saved version:
                  <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
                    {netsuiteDrift.diffs.map(d => (
                      <li key={d.inventoryId}>
                        {d.name || d.sku || d.inventoryId}: PO {d.poQty ?? "not on PO"}{d.poRate != null ? ` @ $${d.poRate.toFixed(2)}` : ""}
                        {" · "}NetSuite {d.nsQty ?? "not in NetSuite"}{d.nsRate != null ? ` @ $${d.nsRate.toFixed(2)}` : ""}
                      </li>
                    ))}
                  </Box>
                </>
              )}
            </Alert>
          )}
        </Box>
      </Box>

      {/* Charge Schedule Preview */}
      {selectedSupplier && (
        <ChargeSchedulePreview
          supplier={selectedSupplier}
          amount={effectiveAmount}
          tariffRatePercent={editingPOTariffRate ?? null}
          placementDate={editingPOPlacementDate ?? null}
          etd={editingPOEtd ?? null}
          paymentDateOverrides={editingPOPaymentDateOverrides ?? null}
          onSetPaymentDateOverride={(trancheOrder, date) => {
            if (!onSetPOPaymentDateOverrides) return;
            const current = editingPOPaymentDateOverrides || {};
            if (date === null) {
              const { [String(trancheOrder)]: _, ...rest } = current;
              onSetPOPaymentDateOverrides(Object.keys(rest).length > 0 ? rest : null);
            } else {
              onSetPOPaymentDateOverrides({ ...current, [String(trancheOrder)]: date });
            }
          }}
          paidTranches={editingPOPaidTranches}
          onTogglePaidTranche={onTogglePaidTranche}
          isUpdatingPaid={isUpdatingPaidStatus}
        />
      )}

      {/* Line Items */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Line Items</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onReplaceLineItems && (
            <Tooltip title="Upload the PO PDF we sent the manufacturer: its lines are read, matched to SKUs, and the quantities (and printed prices / product #s) applied here">
              <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => setIsImportPdfOpen(true)}>
                Match to PO PDF…
              </Button>
            </Tooltip>
          )}
          {onReplaceLineItems && pickerWro && (
            <Tooltip title={wroQuantityMismatch
              ? "Set this PO's lines to what WRO " + pickerWro.wroId + " expects (quantities, missing lines added, dropped lines removed)"
              : "This PO already matches WRO " + pickerWro.wroId}>
              <span>
                <Button size="small" variant="outlined" onClick={matchPoToWro} disabled={!wroQuantityMismatch}>
                  Match to WRO {pickerWro.wroId}
                </Button>
              </span>
            </Tooltip>
          )}
          {pendingSuggestionCount > 0 && (
            <Tooltip title="Fills every empty price, Danican product # and tariff rate with the SKU master's current value — you can still edit them after">
              <Button size="small" variant="outlined" onClick={acceptAllSuggestions}>
                Accept {pendingSuggestionCount} suggested value{pendingSuggestionCount === 1 ? "" : "s"}
              </Button>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 300 }}>Inventory</TableCell>
              <TableCell sx={{ width: 140 }}>Product # (Danican)</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Quantity</TableCell>
              <TableCell align="right" sx={{ width: 130 }}>Unit Price</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Tariff</TableCell>
              <TableCell align="right" sx={{ width: 110 }}>Line Total</TableCell>
              <TableCell align="center" sx={{ width: 60 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {editingPOLineItems.map((lineItem, index) => (
              <TableRow key={index}>
                <TableCell>
                  <Autocomplete
                    size="small"
                    // A line can carry an item the page's inventory list does not (its list is
                    // built for other purposes); the line's own id/name still has to show.
                    options={lineItem.inventoryId && !inventoryOptions.some(opt => opt.id === lineItem.inventoryId)
                      ? [{ id: lineItem.inventoryId, name: lineItem.inventoryName ?? null, skus: lineItem.sku ? [lineItem.sku] : [] }, ...inventoryOptions]
                      : inventoryOptions}
                    isOptionEqualToValue={(option, value) => option.id === value.id}
                    getOptionLabel={(option) => option.name ? `${option.id} - ${option.name}` : option.id}
                    filterOptions={(options, { inputValue }) => {
                      if (!inputValue) return options;
                      const lower = inputValue.toLowerCase();
                      return options.filter(opt =>
                        opt.id.toLowerCase().includes(lower) ||
                        (opt.name && opt.name.toLowerCase().includes(lower)) ||
                        opt.skus.some(sku => sku.toLowerCase().includes(lower))
                      );
                    }}
                    value={inventoryOptions.find(opt => opt.id === lineItem.inventoryId)
                      ?? (lineItem.inventoryId ? { id: lineItem.inventoryId, name: lineItem.inventoryName ?? null, skus: lineItem.sku ? [lineItem.sku] : [] } : null)}
                    onChange={(_, newValue) => {
                      const knownCost = catalogPriceFor(newValue?.id);
                      // Pre-fill from the new SKU's current price, unless the
                      // line carries a price that was typed by hand — i.e. one
                      // that is not just the previous SKU's price carried over.
                      const previousCost = catalogPriceFor(lineItem.inventoryId);
                      const priceIsInherited = lineItem.unitPrice == null
                        || lineItem.unitPrice === previousCost;
                      onUpdateLineItem(index, {
                        inventoryId: newValue?.id || "",
                        inventoryName: newValue?.name || undefined,
                        // A different item has to be confirmed again
                        skuConfirmed: false,
                        skuConfirmedAt: null,
                        ...(priceIsInherited && knownCost != null ? { unitPrice: knownCost } : {}),
                      });
                    }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Search inventory by name, ID, or SKU..." />
                    )}
                    sx={{ minWidth: 300 }}
                  />
                </TableCell>
                <TableCell>
                  <TextField
                    size="small"
                    placeholder={lineSuggestions[index]?.productNumber || "e.g. SBL-1021"}
                    value={lineItem.manufacturerProductNumber ?? ""}
                    onChange={(e) => {
                      onUpdateLineItem(index, { manufacturerProductNumber: e.target.value || null });
                    }}
                    InputProps={lineSuggestions[index]?.productNumber ? {
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Use the SKU master's Danican product #">
                            <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                              onClick={() => onUpdateLineItem(index, { manufacturerProductNumber: lineSuggestions[index].productNumber! })}>
                              Use
                            </Button>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    } : undefined}
                    sx={{ width: 170 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={lineItem.quantity || ""}
                    onChange={(e) => {
                      const qty = parseInt(e.target.value, 10);
                      onUpdateLineItem(index, { quantity: isNaN(qty) ? 0 : qty });
                    }}
                    inputProps={{ min: 1 }}
                    sx={{ width: 100 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    placeholder={lineSuggestions[index]?.price != null ? lineSuggestions[index].price!.toFixed(2) : undefined}
                    value={lineItem.unitPrice ?? ""}
                    onChange={(e) => {
                      const price = e.target.value === "" ? null : parseFloat(e.target.value);
                      onUpdateLineItem(index, { unitPrice: price != null && !isNaN(price) ? price : null });
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">$</InputAdornment>,
                      ...(lineSuggestions[index]?.price != null && {
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title="Use the SKU master's current price for this PO's container type">
                              <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                                onClick={() => onUpdateLineItem(index, { unitPrice: lineSuggestions[index].price! })}>
                                Use
                              </Button>
                            </Tooltip>
                          </InputAdornment>
                        ),
                      }),
                    }}
                    inputProps={{ min: 0, step: 0.01 }}
                    sx={{ width: 160 }}
                  />
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    placeholder={lineSuggestions[index]?.tariff ? String(toPercent(lineSuggestions[index].tariff!.rate)) : undefined}
                    value={toPercent(lineItem.tariffRate) ?? ""}
                    onChange={(e) => {
                      const percent = e.target.value === "" ? null : parseFloat(e.target.value);
                      onUpdateLineItem(index, { tariffRate: percent != null && !isNaN(percent) ? fromPercent(percent) : null });
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          {lineSuggestions[index]?.tariff ? (
                            <Tooltip title={`Use ${toPercent(lineSuggestions[index].tariff!.rate)}% — ${lineSuggestions[index].tariff!.source}`}>
                              <Button size="small" sx={{ minWidth: 0, px: 0.5 }}
                                onClick={() => onUpdateLineItem(index, { tariffRate: lineSuggestions[index].tariff!.rate })}>
                                Use
                              </Button>
                            </Tooltip>
                          ) : "%"}
                        </InputAdornment>
                      ),
                    }}
                    inputProps={{ min: 0, max: 100, step: 0.1 }}
                    sx={{ width: 110 }}
                  />
                </TableCell>
                <TableCell align="right">
                  {lineItem.unitPrice != null && lineItem.quantity > 0
                    ? `$${(lineItem.quantity * lineItem.unitPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : "—"}
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => onRemoveLineItem(index)}
                    disabled={editingPOLineItems.length <= 1}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            {(() => {
              const lines = editingPOLineItems.filter(li => li.inventoryId && li.quantity > 0);
              const units = lines.reduce((sum, li) => sum + li.quantity, 0);
              const priced = lines.filter(li => li.unitPrice != null);
              const total = priced.reduce((sum, li) => sum + li.quantity * (li.unitPrice as number), 0);
              const unpriced = lines.length - priced.length;
              return (
                <TableRow sx={{ "& td": { fontWeight: 700, borderBottom: "none" } }}>
                  <TableCell>Total · {lines.length} line{lines.length === 1 ? "" : "s"}</TableCell>
                  <TableCell />
                  <TableCell align="right">{units.toLocaleString()}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 400, color: "text.secondary" }}>
                    {unpriced > 0 ? `${unpriced} unpriced` : ""}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 400, color: "text.secondary" }}>
                    {weightedTariffPercent != null ? `${weightedTariffPercent}% avg` : ""}
                  </TableCell>
                  <TableCell align="right">
                    {priced.length > 0
                      ? `$${total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}${unpriced > 0 ? "+" : ""}`
                      : "—"}
                  </TableCell>
                  <TableCell />
                </TableRow>
              );
            })()}
          </TableFooter>
        </Table>
      </TableContainer>

      <Button
        variant="outlined"
        size="small"
        startIcon={<AddIcon />}
        onClick={onAddLineItem}
      >
        Add Line Item
      </Button>

      {onGeneratePdf && (
        <EmailPoDialog
          open={isEmailPoOpen}
          onClose={() => setIsEmailPoOpen(false)}
          poNumber={editingPONumber}
        />
      )}

      {onNetSuitePushed && (
        <PushToNetSuiteDialog
          open={isNetSuiteOpen}
          onClose={() => setIsNetSuiteOpen(false)}
          poNumber={editingPONumber}
          onPushed={onNetSuitePushed}
          onUpdated={onRefreshNetSuiteDrift}
        />
      )}

      {onReplaceLineItems && (
        <ImportPoPdfDialog
          open={isImportPdfOpen}
          onClose={() => setIsImportPdfOpen(false)}
          // A new PO has no number yet: the upload still needs a path segment, and
          // the PDF's own number and date fill the blanks in the form.
          poNumber={editingPONumber.trim() || "new"}
          newPo={!editingPONumber.trim()}
          inventoryOptions={inventoryOptions}
          currentLines={editingPOLineItems}
          catalogPriceFor={catalogPriceFor}
          suggestedProductNumberFor={suggestedProductNumberFor}
          onApply={(lines, { skusChanged, poNumber, poDate }) => {
            onReplaceLineItems(skusChanged ? lines.map(li => ({ ...li, skuConfirmed: true })) : lines);
            if (!editingPONumber.trim() && poNumber) onSetPONumber(poNumber);
            if (!editingPOPlacementDate && poDate) onSetPOPlacementDate?.(poDate);
          }}
        />
      )}

      {onWroCreated && pickerWro && (
        <UpdateWroDialog
          open={isUpdateWroOpen}
          onClose={() => setIsUpdateWroOpen(false)}
          wro={pickerWro}
          poNumber={editingPONumber}
          poItems={editingPOLineItems}
          quantitiesLockedReason={selectedWroSharedWithOthers
            ? `WRO ${pickerWro.wroId} also carries units for another PO — change its quantities in ShipBob directly so both POs' shares are kept. Its date, PO # and container number can still be changed here.`
            : null}
          onUpdated={onWroCreated}
        />
      )}

      <Dialog open={isLinkWroOpen} onClose={isLinkingWro ? undefined : () => setIsLinkWroOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Link a ShipBob WRO to {editingPONumber}</DialogTitle>
        <DialogContent>
          {linkWroError && <Alert severity="error" sx={{ mb: 2 }}>{linkWroError}</Alert>}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the WRO number from ShipBob. It's pulled in immediately and pinned to this PO.
          </Typography>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="WRO #"
            placeholder="e.g. 981173"
            value={linkWroInput}
            onChange={(e) => setLinkWroInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleLinkWroByNumber(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsLinkWroOpen(false)} disabled={isLinkingWro}>Cancel</Button>
          <Button variant="contained" onClick={handleLinkWroByNumber} disabled={isLinkingWro || !/^\d+$/.test(linkWroInput.trim())}>
            {isLinkingWro ? "Linking…" : "Link WRO"}
          </Button>
        </DialogActions>
      </Dialog>

      {onWroCreated && (
        <CreateWroDialog
          open={isCreateWroOpen}
          onClose={() => setIsCreateWroOpen(false)}
          poNumber={editingPONumber}
          arriveByDate={editingPODate}
          countryOfDestination={editingPOCountryOfDestination}
          containerType={editingPOContainerType}
          lineItems={editingPOLineItems}
          defaultFulfillmentCenterId={editingPOShipToFcId}
          recentFulfillmentCenterNames={
            [...wroOptions]
              .sort((a, b) => (b.lastUpdatedDate || "").localeCompare(a.lastUpdatedDate || ""))
              .map(w => w.fulfillmentCenterName)
              .filter((name): name is string => Boolean(name))
          }
          onCreated={onWroCreated}
        />
      )}
    </>
  );
}
